// CountUp — small animated number ticker. When `value` changes, the displayed
// number rolls smoothly from its previous value to the new one. Optional
// `format` shapes the rendered string (e.g. money() or "{n}%"). Pure RAF-based
// — no framer-motion dependency — so it scales to mobile without jank.
//
// Behaviour notes:
//   • First render: shows the target value instantly (no startup tick from 0).
//   • Big jumps cap at ~700ms so the kid doesn't wait.
//   • Sign-aware: animates through negative numbers correctly.

import { useEffect, useRef, useState, type CSSProperties } from 'react';

const DEFAULT_DURATION_MS = 500;
const MAX_DURATION_MS = 700;

export type CountUpProps = {
  value: number;
  /** Render function: (currentTickValue) → string. Default = round + locale. */
  format?: (n: number) => string;
  /** Optional inline styles forwarded to the wrapping span. */
  style?: CSSProperties;
  className?: string;
};

export function CountUp({ value, format, style, className }: CountUpProps) {
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(value);
  const startRef = useRef<number | null>(null);
  const targetRef = useRef(value);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    // Same value? No animation. Avoids constant ticking on re-renders.
    if (value === targetRef.current && value === display) return;
    fromRef.current = display;
    targetRef.current = value;
    startRef.current = null;

    // Scale duration with the size of the jump so big leaps don't crawl
    // and tiny tweaks aren't sluggish — cap both ends.
    const jump = Math.abs(value - display);
    const dur = Math.min(MAX_DURATION_MS, Math.max(180, Math.min(DEFAULT_DURATION_MS, 60 + jump * 6)));

    const tick = (now: number) => {
      if (startRef.current === null) startRef.current = now;
      const elapsed = now - startRef.current;
      const t = Math.min(1, elapsed / dur);
      // easeOutCubic — fast start, soft landing on the final value.
      const eased = 1 - Math.pow(1 - t, 3);
      const next = fromRef.current + (targetRef.current - fromRef.current) * eased;
      setDisplay(t >= 1 ? targetRef.current : next);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
    // We intentionally don't depend on `display` — the tick reads it via ref-like
    // pattern (fromRef snapshot at start) so the animation runs to completion
    // without restarting on every intermediate render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const text = (format ?? defaultFormat)(display);
  return (
    <span className={className} style={style} aria-live="polite">
      {text}
    </span>
  );
}

function defaultFormat(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}
